import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useQueryClient } from "@tanstack/react-query";

import { userKeys } from "hooks/api/query-keys";
import {
  clearCurrentUserCache,
  CurrentUserProvider,
  fetchCurrentUser,
  updateCurrentUserCache,
  type UserAttributes,
} from "hooks/api/use-current-user";

import AuthenticatedApp from "containers/authenticated-app";
import LoginPage from "containers/login-page/login-page";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";

import { colors } from "design-tokens";

const AppRoutesWrapper = () => {
  const { inProgress, accounts, instance } = useMsal();
  const isMsalAuthenticated = accounts.length > 0;
  const queryClient = useQueryClient();
  const cachedCurrentUser = queryClient.getQueryData<UserAttributes>(userKeys.me());
  const [currentUser, updateCurrentUser] = useState<UserAttributes | null>(cachedCurrentUser ?? null);
  const isCurrentUserPresent = !!currentUser;
  const [isCurrentUserResolved, setIsCurrentUserResolved] = useState(isCurrentUserPresent);

  useEffect(() => {
    if (!isMsalAuthenticated || inProgress !== InteractionStatus.None || isCurrentUserResolved) {
      return;
    }
    (async () => {
      try {
        const fetchedUser = await queryClient.fetchQuery({
          queryKey: userKeys.me(),
          queryFn: fetchCurrentUser,
        });
        updateCurrentUserCache(queryClient, fetchedUser);
        updateCurrentUser(fetchedUser);
      } catch {
        // Break the reconnect loop: MSAL may still have an account cached but token acquisition is no longer possible.
        // Clearing cache makes accounts empty and prevents repeated fetch→fail cycles (and repeated /management/info calls on LoginPage).
        try {
          await instance.clearCache({ account: instance.getActiveAccount() });
        } catch {
          // ignore
        }
        clearCurrentUserCache(queryClient);
        updateCurrentUser(null);
      } finally {
        setIsCurrentUserResolved(true);
      }
    })();
  }, [inProgress, instance, isMsalAuthenticated, isCurrentUserResolved, queryClient]);

  if (inProgress !== InteractionStatus.None || (isMsalAuthenticated && !isCurrentUserResolved)) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" color={colors.aqua[500]} />
      </div>
    );
  }

  return isCurrentUserPresent && isMsalAuthenticated ? (
    <CurrentUserProvider user={currentUser}>
      <AuthenticatedApp />
    </CurrentUserProvider>
  ) : (
    <LoginPage />
  );
};

export default AppRoutesWrapper;
